import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import Icon from './Icon.jsx'; // the audience pill ships on a drawn icon, not an emoji (test/iconSystem ratchet)

// "What did this person actually get, and why?" — the screen that did not exist
// on the day it was needed, when that question was answered from screenshots and
// inference instead of data.
//
// Reads /api/diagnose/:entityId/customer. Everything here comes from the
// per-recipient ledgers, which are COMPLETE — unlike the mail log, which keeps
// only the most recent ~5,000 sends platform-wide and had already evicted a
// whole day's history by mid-afternoon. That distinction is drawn on screen
// rather than left for someone to discover the hard way.

const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };

export default function CustomerDiagnose({ entityId, email, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!email) return;
    setD(null); setErr('');
    api.diagnoseCustomer(entityId, email).then(setD).catch((e) => setErr(e.message || 'Could not load'));
  }, [entityId, email]);

  if (!email) return null;
  return (
    <div style={panel}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14, fontWeight: 800 }}>{email}</span>
        {onClose && <button type="button" style={{ ...mini, marginLeft: 'auto' }} onClick={onClose}>✕ Close</button>}
      </div>

      {err && <p style={{ color: 'var(--error,#ef4444)', fontSize: 13 }}>{err}</p>}
      {!d && !err && <p style={{ color: 'var(--muted)', fontSize: 13 }}>Looking them up…</p>}

      {d && (
        <>
          {/* Deliverability first: it explains most "why did they stop getting
              emails?" questions, and no amount of campaign detail matters if we
              physically cannot reach them. */}
          <div style={{
            ...box, marginBottom: 12,
            background: d.deliverable.ok ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
            borderColor: d.deliverable.ok ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.45)',
          }}>
            {d.deliverable.ok
              ? <span style={{ fontWeight: 700, color: 'var(--success,#10b981)' }}>✓ We can email this person</span>
              : (
                <span style={{ fontWeight: 700, color: 'var(--error,#ef4444)' }}>
                  ✗ Suppressed — {d.deliverable.reason} since {fmt(d.deliverable.since)}
                  {d.deliverable.detail ? <span style={{ fontWeight: 500, color: 'var(--muted)' }}> · {d.deliverable.detail}</span> : null}
                  <div style={{ fontWeight: 500, color: 'var(--muted)', marginTop: 3 }}>Nothing will be delivered to them until this clears — every campaign below will skip them.</div>
                </span>
              )}
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, fontSize: 12.5 }}>
            <Stat label="Campaigns" value={d.totals.campaignsTouched} />
            <Stat label="Messages sent" value={d.totals.messagesSent} />
            <Stat label="Opens" value={d.totals.opens} />
            <Stat label="Clicks" value={d.totals.clicks} />
          </div>

          {d.campaigns.length === 0 && <p style={{ color: 'var(--muted)', fontSize: 13 }}>No campaign of this client has ever touched this address.</p>}

          {d.campaigns.map((c) => (
            <div key={c.actionId} style={{ ...box, marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>{c.campaign}</span>
                <span style={pill}>{c.shape.type}{c.shape.messages ? ` · ${c.shape.messages} messages` : ''}</span>
                <span style={{ ...pill, background: 'rgba(128,128,128,0.14)' }}>{c.campaignStatus}</span>
                {/* The segment NAME — the thing the mail log could never show. */}
                <span style={{ ...pill, background: 'rgba(14,165,233,0.14)', color: '#0369a1', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Icon name="segments" size={12} /> {c.audience.name}{c.audience.size > 0 ? ` (${c.audience.size.toLocaleString()})` : ''}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6, fontSize: 12.5, color: 'var(--muted)' }}>
                <span><b style={{ color: 'var(--text)' }}>{c.sent}</b> sent{c.lastSentAt ? ` · last ${fmt(c.lastSentAt)}` : ''}</span>
                <span><b style={{ color: 'var(--text)' }}>{c.opened}</b> opened{c.lastOpenAt ? ` · ${fmt(c.lastOpenAt)}` : ''}</span>
                <span><b style={{ color: 'var(--text)' }}>{c.clicked}</b> clicked{c.lastClickAt ? ` · ${fmt(c.lastClickAt)}` : ''}</span>
              </div>

              {c.enrolment && (
                <div style={{ marginTop: 6, fontSize: 12.5 }}>
                  <span style={{ color: 'var(--muted)' }}>In this journey they are </span>
                  <b>{c.enrolment.status}</b>
                  {c.enrolment.status === 'active' && <span style={{ color: 'var(--muted)' }}>, standing on <b style={{ color: 'var(--text)' }}>{c.enrolment.standingOn}</b>, next due {fmt(c.enrolment.nextDue)}</span>}
                  {c.enrolment.status === 'converted' && <span style={{ color: 'var(--muted)' }}> on {fmt(c.enrolment.convertedAt)} — they leave the journey at that point</span>}
                  {/* "Next due" is only when the engine will next LOOK at them — on a
                      decision that is every few minutes, and it says nothing about
                      whether they are stuck. The WINDOW is the fact that decides it. */}
                  {c.enrolment.status === 'active' && <WindowState at={c.enrolment.windowCloses} /> }
                  <div style={{ color: 'var(--muted)', marginTop: 2 }}>
                    Enrolled {fmt(c.enrolment.enrolledAt)}
                    {c.path?.length ? <> · path taken: <code style={code}>{c.path.join(' → ')}</code></> : null}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Say plainly what this list is and is not, so an empty one is never
              read as "we never emailed them". */}
          <div style={{ ...box, marginTop: 12 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>Recent mail log ({d.recentMailLog.rows.length})</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 6 }}>{d.recentMailLog.note}</div>
            {/* WHAT SENT IT. mail_log has always recorded `kind` and recipientLog
                has always returned it — this list just dropped it, so a send that
                matched none of the campaigns above was unexplainable: you could
                not tell a campaign from a test, a digest or an alert, and the
                obvious conclusion ("but every campaign is paused!") was the wrong
                one, because most kinds are not campaigns at all (asked 2026-09-03). */}
            {d.recentMailLog.rows.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12, padding: '4px 0', borderTop: i ? '1px solid var(--hairline)' : 'none' }}>
                <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{fmt(r.at)}</span>
                <KindChip kind={r.kind} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.subject}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 700, whiteSpace: 'nowrap', color: r.status === 'sent' ? 'var(--success,#10b981)' : 'var(--muted)' }}>{r.status}</span>
              </div>
            ))}
            {/* The question this list gets asked is always the same one: "every
                campaign is paused, so why did that go out?" Answer it here. */}
            {d.recentMailLog.rows.some((r) => !CAMPAIGN_KINDS.has(r.kind)) && (
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 8, lineHeight: 1.5, borderTop: '1px solid var(--hairline)', paddingTop: 7 }}>
                Rows above that are <b>not</b> tagged <code style={code}>campaign</code> did not come from a campaign, so pausing every campaign
                would not have stopped them — a digest, an alert, a login or a notification sends on its own schedule. A <code style={code}>test</code> row
                is a test send (its subject also carries <code style={code}>[TEST]</code>). A <code style={code}>campaign</code> row that matches none of
                the campaigns listed above means that campaign has since been deleted — the send stays in this log, but there is no campaign left to show.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Kinds that really are a campaign send. Everything else in mail_log —
// digests, alerts, auth mail, notifications, tests — is sent by something with
// its own schedule, which pausing a campaign has no effect on.
const CAMPAIGN_KINDS = new Set(['campaign', 'action', 'after_campaign', 'campaign_draft', 'campaign-approval']);
function KindChip({ kind }) {
  const k = kind || 'other';
  const isCampaign = CAMPAIGN_KINDS.has(k);
  const isTest = k === 'test';
  const tone = isTest
    ? { bg: 'rgba(245,158,11,0.16)', c: '#b45309', t: 'A TEST send — it went only to the address you typed, and never counts as a campaign send' }
    : isCampaign
      ? { bg: 'rgba(124,58,237,0.12)', c: 'var(--brand)', t: 'Sent by a campaign. If no campaign above claims it, that campaign has since been deleted.' }
      : { bg: 'rgba(128,128,128,0.14)', c: 'var(--muted)', t: 'NOT a campaign — this kind of mail sends on its own schedule, so pausing campaigns does not stop it' };
  return (
    <span title={tone.t} style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 800, borderRadius: 980, padding: '1px 7px', background: tone.bg, color: tone.c, fontFamily: 'ui-monospace, monospace' }}>{k}</span>
  );
}

function Stat({ label, value }) {
  return (
    <span><b style={{ fontSize: 16 }}>{Number(value || 0).toLocaleString()}</b> <span style={{ color: 'var(--muted)' }}>{label}</span></span>
  );
}

const panel = { background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 12, padding: 14, marginBottom: 14 };
const box = { border: '1px solid var(--hairline)', borderRadius: 10, padding: '9px 11px' };
const pill = { fontSize: 10.5, fontWeight: 700, borderRadius: 980, padding: '1px 8px', background: 'rgba(245,158,11,0.16)', color: '#b45309' };
// The state of a decision's WAIT WINDOW, said plainly. Three answers, and only
// one of them is normal:
//   in the future — waiting, exactly as designed;
//   in the past    — they should already have moved, so nothing below this
//                    decision is going out and something is wrong;
//   blank          — worse and completely invisible until now: the engine opens a
//                    FRESH window on every tick when wait_until is empty (see the
//                    decision branch in server/journeys.js), so the person never
//                    expires and waits forever while every screen reads "waiting".
function WindowState({ at }) {
  if (!at) return (
    <div style={{ marginTop: 3, fontSize: 12, fontWeight: 700, color: '#b91c1c' }}
      title="With no stored window the engine opens a new one every time it looks at them, so this person can never time out of the decision. They will wait here forever.">
      No wait window stored — they can never time out of this check
    </div>
  );
  const t = Date.parse(at);
  const past = Number.isFinite(t) && t <= Date.now();
  return (
    <div style={{ marginTop: 3, fontSize: 12, fontWeight: past ? 800 : 600, color: past ? '#b91c1c' : 'var(--muted)' }}
      title={past ? 'Their window has closed but they are still standing here, so the steps below this check are not going out.' : 'They leave the moment a branch matches, or when this window closes.'}>
      {past ? 'Window CLOSED ' : 'Window closes '}{fmt(at)}{past ? ' — they should already have moved on' : ''}
    </div>
  );
}
const code = { fontFamily: 'ui-monospace, monospace', fontSize: 11.5, background: 'rgba(128,128,128,0.12)', borderRadius: 5, padding: '1px 5px' };
const mini = { padding: '5px 12px', background: 'rgba(128,128,128,0.10)', color: 'var(--text)', border: '1px solid var(--hairline)', borderRadius: 980, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
